import { LAB_UNAVAILABLE_STATES } from '@/constants/funnel';
import { ROUTE_PAYMENT } from '@/constants/routes';
import { US_STATES } from '@/constants/us-states';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { getFunnelLead, useFunnelLead } from '@/hooks/useFunnelLead';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { JoinFunnelLayout } from '../JoinFunnelLayout';
import { ValuePropCard } from '../components/ValuePropCard';
import { FUNNEL_COPY } from '../funnel-copy';
import { stepRoutePath, useFunnelVariant } from '../routes';

function isLabUnavailable(value: string): boolean {
  return (LAB_UNAVAILABLE_STATES as readonly string[]).includes(value);
}

interface LabUnavailableWarningProps {
  state: string;
}

function LabUnavailableWarning({ state }: LabUnavailableWarningProps) {
  return (
    <div
      className="flex items-start gap-3 rounded-2xl border-1 border-solid border-amber-300 bg-amber-50 px-4 py-3"
      role="status"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="mt-0.5 flex-shrink-0 text-amber-600"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <div className="flex flex-col gap-1">
        <span className="text-catch-dark-blue-90 text-sm font-bold">Labs aren&rsquo;t available in {state} yet.</span>
        <span className="text-sm text-gray-700">
          You can still join Catch and upload your own bloodwork to get a complete picture of your risk.
        </span>
      </div>
    </div>
  );
}

export function JoinStep3() {
  const variant = useFunnelVariant();
  const copy = FUNNEL_COPY[variant].step3;
  const navigate = useNavigate();
  const { updateLead } = useFunnelLead();
  const analytics = useAnalyticsCalls();

  // Initialize from localStorage funnel-lead cache (see JoinStep1 for the
  // rationale — synchronous read, no useEffect hydration race).
  const [state, setState] = useState<string>(() => getFunnelLead()?.state ?? '');

  // Per-step page view is fired globally by useAnalyticsPageViews with
  // funnel_step + positioning_variant attached.

  // Fire the lab-unavailable analytics exactly once per state selection.
  const lastWarnedStateRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isLabUnavailable(state)) {
      lastWarnedStateRef.current = null;
      return;
    }
    if (lastWarnedStateRef.current === state) return;
    lastWarnedStateRef.current = state;
    analytics.callFunnelLabUnavailableShown(state, variant);
  }, [state, variant, analytics]);

  const handleSelect = async (value: string) => {
    setState(value);
    if (!value) return;
    try {
      await updateLead({ state: value });
      analytics.callFunnelFieldCaptured({
        field: 'state',
        value,
        positioningVariant: variant,
      });
    } catch {
      // Surface only at Continue if it matters.
    }
  };

  const handleContinue = () => {
    if (!state) return;
    navigate(ROUTE_PAYMENT);
  };

  const handleBack = () => {
    navigate(stepRoutePath(variant, 2));
  };

  const isLabWarning = isLabUnavailable(state);
  const stateName = US_STATES.find((s) => s.value === state)?.label ?? state;

  return (
    <JoinFunnelLayout step={3} onBack={handleBack} onContinue={handleContinue} isContinueDisabled={!state}>
      <div className="flex flex-col gap-3">
        <h1 className="text-catch-dark-blue-90 text-3xl leading-tight font-bold">{copy.header}</h1>
        <p className="text-base text-gray-500">{copy.subheader}</p>
      </div>

      <div className="flex flex-col gap-4">
        <div className="relative">
          <select
            id="state"
            aria-label="Select a state"
            value={state}
            onChange={(e) => handleSelect(e.target.value)}
            className="text-catch-dark-blue-90 focus:ring-catch-light-blue h-14 w-full appearance-none rounded-2xl border-1 border-solid border-gray-200 bg-white p-[0_40px_0_16px] text-base focus:ring-2 focus:outline-none"
          >
            <option value="">Select a state</option>
            {US_STATES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <svg
            className="pointer-events-none absolute top-1/2 right-4 h-5 w-5 -translate-y-1/2 text-gray-500"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
        {isLabWarning && <LabUnavailableWarning state={stateName} />}
      </div>

      <ValuePropCard copy={copy.valueProp} />
    </JoinFunnelLayout>
  );
}

export default JoinStep3;
